import { AuthenticationStorageDatasource } from "@authentication/data/datasources/authentication-storage.datasource";
import { InternalInvalidSessionUserError } from "@authentication/domain/errors/internal/internal-invalid-session-user.error";
import type { AuthenticatedUser } from "@authentication/domain/models/authenticated-user.model";
import type { UserSessionRepository } from "@authentication/domain/repositories/user-session.repository";
import type { Nullable } from "@iot-platform/core";
import { FallbackError } from "@iot-platform/core";
import { UserDatasource } from "@iot-platform/user";
import { inject, injectable } from "inversify";
import { errAsync, type ResultAsync } from "neverthrow";
import { BehaviorSubject, Observable } from "rxjs";

@injectable()
export class UserSessionImplRepository implements UserSessionRepository {
    constructor(
        @inject(UserDatasource)
        private readonly userDatasource: UserDatasource,
        @inject(AuthenticationStorageDatasource)
        private readonly authenticationStorageDatasource: AuthenticationStorageDatasource,
    ) {}

    private _user: BehaviorSubject<Nullable<AuthenticatedUser>> =
        new BehaviorSubject<Nullable<AuthenticatedUser>>(null);

    get user(): Observable<Nullable<AuthenticatedUser>> {
        return this._user;
    }

    get userSync(): Nullable<AuthenticatedUser> {
        return this._user.value;
    }

    get userSyncSafe(): AuthenticatedUser {
        if (!this.userSync) throw new InternalInvalidSessionUserError();

        return this.userSync;
    }

    setUser(authenticatedUser: Nullable<AuthenticatedUser>): void {
        this._user.next(authenticatedUser);
    }

    loadCurrentUser(): ResultAsync<AuthenticatedUser, FallbackError> {
        const token = this.getAccessToken();

        if (!token) return errAsync(new FallbackError());

        return this.userDatasource.fetchCurrentUser().map((user) => {
            this.setUser(user);
            return user;
        });
    }

    getAccessToken(): Nullable<string> {
        return this.authenticationStorageDatasource.getAccessToken();
    }
}
